// Boutiques — /shops. The one place an account connects its Shopify and
// PrestaShop shops; the orders page, the parcel monitor (notes, tracking
// numbers written on the order) and the catalogue use these connections and
// never ask for shop credentials themselves (Backend docs/shop-connections.md).
// One connection = one list row (state, uses with their rights, import);
// « À vérifier » gathers what needs a person; disconnecting asks first and
// names what it removes.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { SiPrestashop, SiShopify } from 'react-icons/si';
import {
    Check, ChevronRight, Download, Package, Pencil, Plus, ShieldCheck, Store, TriangleAlert, Unplug,
} from 'lucide-react';
import ShopForm from './ShopForm';
import { CATALOG_USES, ORDER_USES, authorize, fail, formatWhen, list, remove, syncOrders, test } from './api';
import { shopState, shopsAttention, shopsSummary } from './shopsSummary';
import {
    Badge, Banner, Card, ConfirmDialog, EmptyState, Kpi, OverflowMenu, PageHeader, Toast,
} from '../../design/ui';
import './ShopConnections.css';

export const PlatformLogo = ({ platform }) => (
    <span className="u-logo" aria-hidden="true">
        {platform === 'shopify' ? <SiShopify color="#95BF47" /> : platform === 'prestashop' ? <SiPrestashop color="#DF0067" /> : <Store strokeWidth={1.8} />}
    </span>
);

const UseBadges = ({ shop, t }) => {
    const missing = new Set(shop.last_test?.missing || []);
    const on = [...ORDER_USES, ...CATALOG_USES].filter((use) => shop.uses?.[use]);
    if (!on.length) return null;
    return (
        <ul className="shops-v2__uses" aria-label={t('shops.usesTitle', 'Ce qu’Unanimity fait avec cette boutique')}>
            {on.map((use) => (
                <li key={use}>
                    <Badge
                        tone={missing.has(use) ? 'danger' : undefined}
                        icon={missing.has(use) ? TriangleAlert : Check}
                        title={missing.has(use) ? t('shops.rightsMissing', 'Droits manquants : voir le dernier test') : undefined}
                    >
                        {t(`shops.uses.${use}.short`, use)}
                    </Badge>
                </li>
            ))}
        </ul>
    );
};

const StateBadge = ({ shop, t }) => {
    const state = shopState(shop);
    if (state === 'install') return <Badge tone="warning" dot>{t('shops.toInstall', 'Application à installer')}</Badge>;
    if (state === 'paused') return <Badge>{t('shops.paused', 'En pause')}</Badge>;
    return <Badge tone="success" dot>{t('shops.active', 'Active')}</Badge>;
};

const ShopRow = ({ shop, canEdit, busy, lang, t, onAction }) => {
    const sync = shop.orders_sync || {};
    const products = shop.products_sync || {};
    const awaitingInstall = shopState(shop) === 'install';
    const canSync = shop.uses?.orders && shop.enabled && !awaitingInstall;
    const facts = [
        shop.shop_domain || shop.base_url,
        shop.client_ref && `${t('shops.clientShort', 'Client')} : ${shop.client_ref}`,
    ].filter(Boolean).join(' · ');

    return (
        <li className="u-row shops-v2__row">
            <PlatformLogo platform={shop.platform} />
            <div className="u-row__main">
                <div className="u-row__title">
                    {shop.name}
                    <StateBadge shop={shop} t={t} />
                </div>
                {facts && <div className="u-row__meta" title={facts}>{facts}</div>}
                <UseBadges shop={shop} t={t} />
                <div className="shops-v2__facts">
                    {shop.uses?.orders && (
                        <span>
                            {t('ordersMoneyUi.shops.imported', '{{total}} commandes importées', { count: Number(sync.total_imported || 0), total: Number(sync.total_imported || 0).toLocaleString(lang) })}
                            {' · '}
                            {sync.running
                                ? t('shops.running', 'en cours…')
                                : t('ordersMoneyUi.shops.lastImport', 'dernier import : {{when}}', { when: formatWhen(sync.last_ok_at, lang) })}
                        </span>
                    )}
                    {shop.uses?.products && (
                        <span>
                            {t('ordersMoneyUi.shops.products', '{{total}} produits au catalogue', { count: Number(shop.impact?.products || 0), total: Number(shop.impact?.products || 0).toLocaleString(lang) })}
                            {products.last_success_at ? ` · ${formatWhen(products.last_success_at, lang)}` : ''}
                            {' · '}
                            <Link className="u-link" to="/catalogue/connections">{t('shops.openCatalog', 'catalogue')}</Link>
                        </span>
                    )}
                    {shop.last_test && (
                        <span className={shop.last_test.ok ? 'shops-v2__ok' : 'shops-v2__ko'}>
                            {t('shops.lastTest', 'Dernier test')} : {shop.last_test.ok ? t('shops.rightsOk', 'Droits vérifiés') : t('shops.rightsKo', 'À corriger')}
                        </span>
                    )}
                </div>
                {sync.customer_fields === false && (
                    <p className="u-help shops-v2__note">{t('shops.customerFieldsHidden', 'Shopify ne transmet pas les coordonnées client à cette application (données client protégées) : les commandes sont importées sans contact ni adresse.')}</p>
                )}
            </div>
            <div className="u-row__side">
                {canEdit && shop.needs_authorization ? (
                    <button type="button" className="u-btn u-btn--secondary u-btn--sm" onClick={() => onAction('authorize', shop)} disabled={Boolean(busy)}>
                        <SiShopify aria-hidden="true" />
                        {awaitingInstall ? t('shops.install', 'Installer l’application') : t('shops.reauthorize', 'Autoriser les nouveaux droits')}
                    </button>
                ) : canSync && (
                    <button type="button" className="u-btn u-btn--secondary u-btn--sm" onClick={() => onAction('sync', shop)} disabled={Boolean(busy)}>
                        <Download aria-hidden="true" />
                        {busy === 'sync' ? t('ordersMoneyUi.shops.syncing', 'Import…') : t('shops.syncNow', 'Importer les commandes')}
                    </button>
                )}
                <OverflowMenu
                    label={t('ordersMoneyUi.moreActions', 'Plus d’actions')}
                    items={[
                        canEdit && !awaitingInstall && { label: t('shops.test', 'Tester les droits'), icon: ShieldCheck, onClick: () => onAction('test', shop), disabled: Boolean(busy) },
                        canEdit && shop.needs_authorization && canSync && { label: t('shops.syncNow', 'Importer les commandes'), icon: Download, onClick: () => onAction('sync', shop), disabled: Boolean(busy) },
                        canEdit && { label: t('shops.edit', 'Modifier'), icon: Pencil, onClick: () => onAction('edit', shop) },
                        canEdit && { separator: true },
                        canEdit && { label: t('shops.disconnect', 'Déconnecter'), icon: Unplug, danger: true, onClick: () => onAction('disconnect', shop), disabled: Boolean(busy) },
                    ]}
                />
            </div>
        </li>
    );
};

const ShopConnections = () => {
    const { t, i18n } = useTranslation();
    const lang = i18n.language || 'fr';
    const [searchParams, setSearchParams] = useSearchParams();
    const [data, setData] = useState(null);
    const [error, setError] = useState('');
    const [editing, setEditing] = useState(null);       // null | 'new' | shop
    const [toast, setToast] = useState(null);
    const [busy, setBusy] = useState({});              // { [shopId]: 'test' | 'sync' | 'authorize' | 'delete' }
    const [disconnecting, setDisconnecting] = useState(null);

    const load = useCallback(async () => {
        try {
            const res = await list();
            setData(res.data);
        } catch (err) {
            setError(fail(err, t('shops.loadError', 'Impossible de charger les boutiques.')));
        }
    }, [t]);

    useEffect(() => { load(); }, [load]);
    // Back from Shopify's install page.
    useEffect(() => {
        const outcome = searchParams.get('shopify');
        if (!outcome) return;
        const shopId = searchParams.get('shop');
        const reasons = {
            signature: t('shops.installErrorSignature', 'La réponse de Shopify n’a pas pu être vérifiée. Recommencez l’installation.'),
            expired: t('shops.installErrorExpired', 'Le lien d’installation a expiré (30 minutes) ou a déjà servi. Recommencez l’installation.'),
            shop_mismatch: t('shops.installErrorShop', 'L’application a été installée sur une autre boutique que celle indiquée. Recommencez depuis la bonne boutique.'),
            denied: t('shops.installErrorDenied', 'L’installation n’a pas été approuvée sur Shopify.'),
            exchange: t('shops.installErrorExchange', 'Shopify n’a pas délivré l’autorisation. Recommencez dans quelques instants.'),
            shop: t('shops.installErrorShop', 'L’application a été installée sur une autre boutique que celle indiquée. Recommencez depuis la bonne boutique.'),
        };
        if (outcome === 'installed' && shopId) {
            (async () => {
                try {
                    const res = await test(shopId);
                    setToast({ ok: res.data.ok, message: res.data.ok
                        ? t('shops.installed', 'Application installée. {{message}}', { message: res.data.message })
                        : res.data.message });
                } catch (e) {
                    setToast({ ok: true, message: t('shops.installedShort', 'Application installée sur la boutique.') });
                }
                load();
            })();
        } else {
            setToast({ ok: false, message: reasons[searchParams.get('reason')] || t('shops.installError', 'L’installation n’a pas abouti.') });
        }
        setSearchParams((prev) => {
            const p = new URLSearchParams(prev);
            ['shopify', 'shop', 'reason'].forEach((k) => p.delete(k));
            return p;
        }, { replace: true });
    }, [searchParams, setSearchParams, t, load]);

    useEffect(() => {
        if (searchParams.get('add') && data?.can_edit) {
            setEditing('new');
            setSearchParams((prev) => { const p = new URLSearchParams(prev); p.delete('add'); return p; }, { replace: true });
        }
    }, [searchParams, data, setSearchParams]);

    // Success toasts leave by themselves; errors stay until closed.
    useEffect(() => {
        if (!toast?.ok) return undefined;
        const timer = setTimeout(() => setToast(null), 5000);
        return () => clearTimeout(timer);
    }, [toast]);

    const saved = async (shop, { created }) => {
        setEditing(null);
        if (created) {
            // A new connection is tested right away: rights are what fail first.
            try {
                const res = await test(shop.id);
                setToast(res.data.ok ? { ok: true, message: res.data.message } : null);
            } catch (e) { /* the row shows the stored verdict */ }
        }
        load();
    };

    const act = async (shop, kind, fn) => {
        setBusy((prev) => ({ ...prev, [shop.id]: kind }));
        try {
            await fn();
        } catch (err) {
            setToast({ ok: false, message: fail(err, t('shops.actionError', 'L’opération a échoué.')) });
        } finally {
            setBusy((prev) => ({ ...prev, [shop.id]: '' }));
        }
    };

    const onAction = (kind, shop) => {
        if (kind === 'edit') { setEditing(shop); return; }
        if (kind === 'disconnect') { setDisconnecting(shop); return; }
        if (kind === 'test') {
            act(shop, 'test', async () => {
                const res = await test(shop.id);
                setToast({ ok: Boolean(res.data.ok), message: res.data.message });
                load();
            });
        }
        if (kind === 'sync') {
            act(shop, 'sync', async () => {
                await syncOrders(shop.id);
                setToast({ ok: true, message: t('shops.syncQueued', 'Import lancé : les nouvelles commandes arrivent dans quelques instants.') });
                load();
            });
        }
        // Shopify: install the app on the shop, or approve the rights new uses need.
        if (kind === 'authorize') {
            act(shop, 'authorize', async () => {
                const res = await authorize(shop.id);
                window.location.assign(res.data.install_url);
            });
        }
    };

    const runDisconnect = async () => {
        const shop = disconnecting;
        await act(shop, 'delete', async () => { await remove(shop.id); load(); });
        setDisconnecting(null);
    };

    const summary = useMemo(() => shopsSummary(data), [data]);
    const attention = useMemo(() => shopsAttention(data), [data]);

    const header = (subtitle) => (
        <PageHeader
            title={t('shops.title', 'Boutiques')}
            subtitle={subtitle}
            actions={data?.can_edit ? (
                <button type="button" className="u-btn u-btn--primary" onClick={() => setEditing('new')}>
                    <Plus aria-hidden="true" />
                    {t('shops.add', 'Connecter une boutique')}
                </button>
            ) : null}
        />
    );

    if (error) {
        return (
            <div className="u-page u-page--narrow shops-v2">
                {header(t('shops.subtitle', 'Connectez une fois chaque boutique Shopify ou PrestaShop : les commandes, la surveillance des colis et le catalogue s’en servent.'))}
                <div className="u-well"><Banner tone="danger">{error}</Banner></div>
            </div>
        );
    }
    if (!data) {
        return (
            <div className="u-page u-page--narrow shops-v2" aria-busy="true" aria-label={t('shops.loading', 'Chargement…')}>
                {header(t('shops.subtitle', 'Connectez une fois chaque boutique Shopify ou PrestaShop : les commandes, la surveillance des colis et le catalogue s’en servent.'))}
                <div className="u-well">
                    <div className="u-kpi-row">{[0, 1, 2].map((i) => <span key={i} className="u-skeleton shops-v2__kpi-skeleton" />)}</div>
                    <span className="u-skeleton shops-v2__list-skeleton" />
                </div>
            </div>
        );
    }

    const shops = data.connections || [];
    const canEdit = Boolean(data.can_edit);
    const subtitle = shops.length
        ? [
            t('ordersMoneyUi.shops.count', '{{count}} boutiques', { count: shops.length }),
            summary.active && t('ordersMoneyUi.shops.activeCount', '{{count}} actives', { count: summary.active }),
            summary.toInstall && t('ordersMoneyUi.shops.installCount', '{{count}} à installer', { count: summary.toInstall }),
            summary.paused && t('ordersMoneyUi.shops.pausedCount', '{{count}} en pause', { count: summary.paused }),
        ].filter(Boolean).join(' · ')
        : t('shops.subtitle', 'Connectez une fois chaque boutique Shopify ou PrestaShop : les commandes, la surveillance des colis et le catalogue s’en servent.');

    const attentionText = (item) => {
        const shop = item.shop;
        switch (item.kind) {
        case 'install':
            return {
                title: t('ordersMoneyUi.shops.attentionInstall', '{{name}} : application à installer', { name: shop.name }),
                detail: t('shops.installPending', 'Installez l’application Unanimity sur cette boutique : Shopify vous demandera d’approuver les droits des usages cochés.'),
                action: canEdit && { label: t('shops.install', 'Installer l’application'), run: () => onAction('authorize', shop) },
            };
        case 'authorize':
            return {
                title: t('ordersMoneyUi.shops.attentionAuthorize', '{{name}} : nouveaux droits à autoriser', { name: shop.name }),
                detail: t('shops.newRights', 'Les usages cochés demandent des droits que Shopify n’a pas encore accordés : autorisez-les.'),
                action: canEdit && { label: t('shops.reauthorize', 'Autoriser les nouveaux droits'), run: () => onAction('authorize', shop) },
            };
        case 'rights':
            return {
                title: t('ordersMoneyUi.shops.attentionRights', '{{name}} : droits manquants', { name: shop.name }),
                detail: shop.last_test.message,
                action: canEdit && { label: t('shops.test', 'Tester les droits'), run: () => onAction('test', shop) },
            };
        case 'import':
            return {
                title: t('ordersMoneyUi.shops.attentionImport', 'L’import des commandes de {{name}} échoue', { name: shop.name }),
                detail: shop.orders_sync.last_error,
                action: canEdit && { label: t('shops.edit', 'Modifier'), run: () => onAction('edit', shop) },
            };
        default:
            return {
                title: t('ordersMoneyUi.shops.attentionMonitoring', 'Les colis des commandes importées ne sont pas surveillés'),
                detail: t('shops.monitoringOff', 'Les commandes sont importées, mais leurs colis ne sont surveillés que si la surveillance colis est active.'),
                link: '/parcel-monitor',
            };
        }
    };

    return (
        <div className="u-page u-page--narrow shops-v2">
            {header(subtitle)}

            <div className="u-well">
                {!canEdit && <Banner tone="info">{t('shops.adminOnly', 'Seul un administrateur du compte peut connecter ou modifier une boutique.')}</Banner>}

                {attention.length > 0 && (
                    <Card
                        icon={TriangleAlert}
                        iconTone="warning"
                        title={t('ordersMoneyUi.attention', 'À vérifier')}
                        badge={<Badge tone="warning">{attention.length}</Badge>}
                        flush
                        className="shops-v2__attention"
                    >
                        <ul className="u-list">
                            {attention.map((item) => {
                                const text = attentionText(item);
                                return (
                                    <li className="u-row" key={item.key}>
                                        <Badge tone={item.tone} dot>
                                            {item.tone === 'danger' ? t('ordersMoneyUi.critical', 'Bloquant') : t('ordersMoneyUi.watch', 'À surveiller')}
                                        </Badge>
                                        <div className="u-row__main">
                                            <div className="u-row__title">{text.title}</div>
                                            {text.detail && <div className="u-row__meta u-row__meta--wrap">{text.detail}</div>}
                                        </div>
                                        <div className="u-row__side">
                                            {text.action && (
                                                <button type="button" className="u-btn u-btn--secondary u-btn--sm" onClick={text.action.run}
                                                    disabled={Boolean(item.shop && busy[item.shop.id])}>
                                                    {text.action.label}
                                                </button>
                                            )}
                                            {text.link && (
                                                <Link className="u-btn u-btn--secondary u-btn--sm" to={text.link}>
                                                    {t('shops.openMonitoring', 'Surveillance colis')}
                                                    <ChevronRight aria-hidden="true" />
                                                </Link>
                                            )}
                                        </div>
                                    </li>
                                );
                            })}
                        </ul>
                    </Card>
                )}

                {shops.length > 0 && (
                    <div className="u-kpi-row">
                        <Kpi
                            icon={Store}
                            label={t('ordersMoneyUi.shops.kpiShops', 'Boutiques connectées')}
                            value={summary.total}
                            context={Object.entries(summary.byPlatform)
                                .map(([platform, n]) => `${(data.platforms || []).find((p) => p.key === platform)?.label || platform} ${n}`).join(' · ')}
                        />
                        {summary.orderShops > 0 && (
                            <Kpi
                                icon={Download}
                                tone="info"
                                label={t('shops.ordersImported', 'Commandes importées')}
                                value={summary.ordersImported.toLocaleString(lang)}
                                context={summary.lastImport
                                    ? t('ordersMoneyUi.shops.kpiLastImport', 'dernier import : {{when}}', { when: formatWhen(summary.lastImport, lang) })
                                    : t('ordersMoneyUi.shops.kpiNoImport', 'aucun import réussi pour l’instant')}
                            />
                        )}
                        {summary.productShops > 0 && (
                            <Kpi
                                icon={Package}
                                tone="neutral"
                                label={t('shops.catalogProducts', 'Produits au catalogue')}
                                value={summary.products.toLocaleString(lang)}
                                context={t('ordersMoneyUi.shops.kpiProductShops', 'de {{count}} boutiques', { count: summary.productShops })}
                            />
                        )}
                        {summary.tested > 0 && (
                            <Kpi
                                icon={ShieldCheck}
                                tone={summary.rightsOk === summary.tested ? 'success' : 'danger'}
                                label={t('ordersMoneyUi.shops.kpiRights', 'Droits vérifiés')}
                                value={summary.rightsOk}
                                unit={`/ ${summary.tested}`}
                                context={summary.rightsOk === summary.tested
                                    ? t('ordersMoneyUi.shops.kpiRightsAll', 'au dernier test de chaque boutique')
                                    : t('ordersMoneyUi.shops.kpiRightsKo', '{{count}} à corriger', { count: summary.tested - summary.rightsOk })}
                                contextTone={summary.rightsOk === summary.tested ? undefined : 'danger'}
                                meter={{ value: summary.rightsOk, max: summary.tested, tone: summary.rightsOk === summary.tested ? 'success' : 'danger', label: t('ordersMoneyUi.shops.kpiRights', 'Droits vérifiés') }}
                            />
                        )}
                    </div>
                )}

                <Card
                    icon={Store}
                    title={t('ordersMoneyUi.shops.listTitle', 'Connexions')}
                    badge={shops.length ? <Badge tone="accent">{shops.length}</Badge> : null}
                    description={t('ordersMoneyUi.shops.listDesc', 'Une connexion par boutique : ses usages, les droits qu’ils demandent et l’import de ses commandes.')}
                    flush
                >
                    {shops.length === 0 ? (
                        <EmptyState
                            icon={Store}
                            title={t('shops.emptyTitle', 'Aucune boutique connectée')}
                            action={canEdit && (
                                <button type="button" className="u-btn u-btn--secondary u-btn--sm" onClick={() => setEditing('new')}>
                                    <Plus aria-hidden="true" />
                                    {t('shops.add', 'Connecter une boutique')}
                                </button>
                            )}
                        >
                            {t('ordersMoneyUi.shops.emptyText', 'Shopify (application Unanimity) et PrestaShop (clé Webservice) : aucun module à installer. Les commandes, la surveillance des colis et le catalogue s’en servent.')}
                        </EmptyState>
                    ) : (
                        <ul className="u-list">
                            {shops.map((shop) => (
                                <ShopRow key={shop.id} shop={shop} canEdit={canEdit} busy={busy[shop.id]} lang={lang} t={t} onAction={onAction} />
                            ))}
                        </ul>
                    )}
                </Card>
            </div>

            {editing && (
                <ShopForm shop={editing === 'new' ? null : editing} meta={data} onSaved={saved} onCancel={() => setEditing(null)} />
            )}

            {disconnecting && (
                <ConfirmDialog
                    title={t('ordersMoneyUi.shops.disconnectTitle', 'Déconnecter « {{name}} » ?', { name: disconnecting.name })}
                    message={disconnecting.impact?.products
                        ? t('ordersMoneyUi.shops.disconnectProducts', 'Unanimity n’importe plus ses commandes et n’écrit plus sur la boutique. Les {{products}} produits du catalogue de cette boutique sont supprimés ; les {{orders}} commandes importées restent.', {
                            products: Number(disconnecting.impact.products).toLocaleString(lang), orders: Number(disconnecting.impact?.orders || 0).toLocaleString(lang),
                        })
                        : t('ordersMoneyUi.shops.disconnectMessage', 'Unanimity n’importe plus ses commandes et n’écrit plus sur la boutique. Les {{orders}} commandes importées restent.', {
                            orders: Number(disconnecting.impact?.orders || 0).toLocaleString(lang),
                        })}
                    confirmLabel={t('shops.disconnect', 'Déconnecter')}
                    cancelLabel={t('shops.cancel', 'Annuler')}
                    icon={Unplug}
                    onConfirm={runDisconnect}
                    onCancel={() => setDisconnecting(null)}
                    busy={busy[disconnecting.id] === 'delete'}
                />
            )}

            {toast && (
                <Toast tone={toast.ok ? 'success' : 'danger'} onClose={() => setToast(null)} closeLabel={t('common.close', 'Fermer')}>
                    {toast.message}
                </Toast>
            )}
        </div>
    );
};

export default ShopConnections;
